import { useMemo, useState } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";
import UserAccessList from "./UserAccessList";
import { normalizeSharingUsers } from "../adapters";
import type { MCPSharing } from "../../../services/agent-tools-service";
import { useTranslation } from "react-i18next";

type Props = {
  connectionId: string;
  sharing: MCPSharing | undefined | null;
  canManage: boolean;
};

const AccessLevelSection = ({ connectionId, sharing, canManage }: Props) => {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const ChevronIcon = open ? ChevronDown : ChevronRight;

  const isUserScope = sharing?.scope === "user";

  const users = useMemo(
    () =>
      isUserScope
        ? normalizeSharingUsers((sharing as { items?: unknown }).items as never)
        : [],
    [isUserScope, sharing],
  );

  const projectName =
    sharing?.scope === "project" ? sharing.name?.trim() : "";

  const activeScope = isUserScope
    ? "user"
    : sharing?.scope === "project"
      ? "project"
      : sharing?.scope === "organization"
        ? "organization"
        : null;

  const scopes = [
    { value: "organization", label: t("mcpPage.accessLevels.organization") },
    { value: "project", label: t("mcpPage.fields.project") },
    { value: "user", label: t("mcpPage.accessLevels.user") },
  ] as const;

  return (
    <section className="border-t border-border p-2">
      <div className="p-0">
        <h3 className="mb-1 text-xs font-medium text-foreground">
          {t("mcpPage.fields.accessLevel")}
        </h3>
        <div
          role="group"
          aria-label={t("mcpPage.fields.accessLevel")}
          className="inline-flex h-10 items-center gap-1 rounded-xl bg-secondary p-1"
        >
          {scopes.map((scope) => (
            <span
              key={scope.value}
              aria-current={scope.value === activeScope ? "true" : undefined}
              className={`flex h-8 items-center rounded-lg px-3 text-sm ${
                scope.value === activeScope
                  ? "bg-background font-medium text-foreground shadow-xs"
                  : "text-secondary-foreground/70"
              }`}
            >
              {scope.label}
            </span>
          ))}
        </div>
      </div>

      {isUserScope ? (
        <div className="mt-1">
          <button
            type="button"
            onClick={() => setOpen((value) => !value)}
            className="flex h-[57px] w-full min-w-0 items-center gap-2 rounded-md text-start text-sm font-medium text-foreground outline-none focus-visible:ring-2 focus-visible:ring-ring"
            aria-expanded={open}
          >
            <ChevronIcon className={`size-4 text-muted-foreground ${i18n.dir() === "rtl" && !open ? "rotate-180" : ""}`} />
            <span className="truncate">
              {t("mcpPage.accessScope.selectedUsers")}
            </span>
            <span className="flex h-4 min-w-4 shrink-0 items-center justify-center rounded-full bg-accent px-1 text-xs text-accent-foreground">
              {users.length}
            </span>
          </button>
          {open && (
            <UserAccessList
              connectionId={connectionId}
              users={users}
              canManage={canManage}
            />
          )}
        </div>
      ) : activeScope === "project" ? (
        <div className="mt-2 flex w-full flex-col gap-1">
          <span
            id={`mcp-project-label-${connectionId}`}
            className="text-xs font-medium text-secondary-foreground"
          >
            {t("mcpPage.fields.project")}
          </span>
          <div
            role="textbox"
            aria-readonly="true"
            aria-labelledby={`mcp-project-label-${connectionId}`}
            className="flex h-9 w-full min-w-0 items-center gap-2 rounded-md border border-input bg-background px-3 text-sm text-foreground shadow-xs"
          >
            <span className="min-w-0 flex-1 truncate">
              {projectName || t("common.notAvailable")}
            </span>
            <ChevronDown
              className="size-4 shrink-0 text-muted-foreground"
              aria-hidden="true"
            />
          </div>
        </div>
      ) : activeScope === null ? (
        <p className="mt-2 text-sm text-muted-foreground" role="status">
          {t("common.notAvailable")}
        </p>
      ) : null}
    </section>
  );
};

export default AccessLevelSection;
